import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import './SettingsMultiple.css';

interface BotConfig {
  id?: number | null;
  enabled: boolean;
  token: string;
  name: string;
  link: string;
  description: string;
  bot_id?: string;
  username?: string;
  webhook_url?: string;
  webhook_token?: string;
  group_id: number | null;
  platform?: string;
  total_messages?: number;
  total_users?: number;
  keyboards_count?: number;
  created_at?: string | null;
  updated_at?: string | null;
  logo_url?: string | null;
}

interface BotsSettings {
  telegram_bots: BotConfig[];
  max_bots: BotConfig[];
}

interface Group {
  id: number;
  group_name: string;
}

interface TestNotice {
  type: 'success' | 'error';
  text: string;
}

const SettingsMultiple: React.FC = () => {
  const { logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [testNotices, setTestNotices] = useState<{ [key: string]: TestNotice }>({});
  const [showTokens, setShowTokens] = useState<{ [key: string]: boolean }>({});
  const [editingBots, setEditingBots] = useState<{ [key: string]: boolean }>({});
  const [groups, setGroups] = useState<Group[]>([]);
  
  const [settings, setSettings] = useState<BotsSettings>({
    telegram_bots: Array(5).fill(null).map(() => ({
      id: null,
      enabled: false,
      token: '',
      name: '',
      link: '',
      description: '',
      bot_id: '',
      username: '',
      webhook_url: '',
      webhook_token: '',
      group_id: null,
      platform: 'telegram',
      total_messages: 0,
      total_users: 0,
      keyboards_count: 0,
      created_at: null,
      updated_at: null,
      logo_url: null,
    })),
    max_bots: Array(5).fill(null).map(() => ({
      id: null,
      enabled: false,
      token: '',
      name: '',
      link: '',
      description: '',
      bot_id: '',
      username: '',
      webhook_url: '',
      webhook_token: '',
      group_id: null,
      platform: 'max',
      total_messages: 0,
      total_users: 0,
      keyboards_count: 0,
      created_at: null,
      updated_at: null,
      logo_url: null,
    }))
  });

  useEffect(() => {
    loadSettings();
    loadGroups();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/settings/bots');
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to load settings:', error);
      setMessage({ type: 'error', text: 'Не удалось загрузить настройки' });
    } finally {
      setLoading(false);
    }
  };

  const loadGroups = async () => {
    try {
      const response = await api.get('/api/groups');
      if (Array.isArray(response.data)) {
        setGroups(response.data);
      }
    } catch (error) {
      console.error('Failed to load groups:', error);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage(null);
      await api.post('/api/settings/bots', settings);
      await loadSettings();
      setMessage({ type: 'success', text: 'Настройки сохранены успешно' });
    } catch (error) {
      console.error('Failed to save settings:', error);
      setMessage({ type: 'error', text: 'Ошибка при сохранении настроек' });
    } finally {
      setSaving(false);
    }
  };

  const handleRefreshWebhook = async (botId: number | null | undefined, botKey: string) => {
    if (!botId) {
      setTestNotices(prev => ({ ...prev, [botKey]: { type: 'error', text: 'Сначала «Сохранить» — бот не зарегистрирован в БД' } }));
      return;
    }
    try {
      const resp = await api.post(`/api/settings/bots/${botId}/refresh-webhook`);
      const okText = resp?.data?.ok
        ? `Webhook обновлён${resp.data.description ? ` (${resp.data.description})` : ''}`
        : `Webhook обновлён${resp.data?.method ? ` (${resp.data.method})` : ''}`;
      setTestNotices(prev => ({ ...prev, [botKey]: { type: 'success', text: okText } }));
    } catch (e: any) {
      const msg = e?.response?.data?.detail || 'Ошибка обновления webhook';
      setTestNotices(prev => ({ ...prev, [botKey]: { type: 'error', text: msg } }));
    }
    setTimeout(() => {
      setTestNotices(prev => { const next = { ...prev }; delete next[botKey]; return next; });
    }, 8000);
  };

  const handleTestBot = async (botType: 'telegram' | 'max', botIndex: number, botKey: string) => {
    try {
      await api.post(`/api/settings/bots/test/${botType}/${botIndex}`);
      setTestNotices(prev => ({
        ...prev,
        [botKey]: {
          type: 'success',
          text: `${botType === 'telegram' ? 'Telegram' : 'MAX'} бот #${botIndex} работает корректно`
        }
      }));
    } catch (error) {
      setTestNotices(prev => ({
        ...prev,
        [botKey]: {
          type: 'error',
          text: `Ошибка проверки ${botType === 'telegram' ? 'Telegram' : 'MAX'} бота #${botIndex}`
        }
      }));
    }

    setTimeout(() => {
      setTestNotices(prev => {
        const next = { ...prev };
        delete next[botKey];
        return next;
      });
    }, 8000);
  };

  const toggleTokenVisibility = (key: string) => {
    setShowTokens(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const toggleEditBot = (key: string) => {
    setEditingBots(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const [selectedBot, setSelectedBot] = useState<{ botType: 'telegram' | 'max'; index: number }>({ botType: 'telegram', index: 0 });
  const [mobileView, setMobileView] = useState<'list' | 'detail'>('list');

  const updateBotField = (botType: 'telegram_bots' | 'max_bots', index: number, field: string, value: any) => {
    setSettings(prev => ({
      ...prev,
      [botType]: prev[botType].map((bot, i) => 
        i === index ? { ...bot, [field]: value } : bot
      )
    }));
  };

  if (loading) {
    return (
      <div className="settings-container">
        <div className="loading">Загрузка настроек...</div>
      </div>
    );
  }

  const tgConfigured = settings.telegram_bots.filter(b => b.id != null).length;
  const maxConfigured = settings.max_bots.filter(b => b.id != null).length;

  return (
    <div className="settings-container">
      <div className="settings-header">
        <button
          className="back-btn"
          onClick={() => window.location.href = '/dashboard'}
        >
          <i className="bi bi-arrow-left"></i>
          Назад
        </button>
        <h1><i className="bi bi-robot"></i> Настройки ботов</h1>
        <div className="header-actions">
          <button className="logout-btn" onClick={logout}>
            <i className="bi bi-box-arrow-right"></i>
            Выход
          </button>
        </div>
      </div>

      {message && (
        <div className={`message ${message.type}`}>
          <i className={`bi bi-${message.type === 'success' ? 'check-circle' : 'exclamation-circle'}`}></i>
          {message.text}
        </div>
      )}

      <div className={`bots-md-layout mobile-view-${mobileView}`}>
        {/* Master: list of all bots */}
        <aside className="bots-md-sidebar">
          <div className="bots-md-sidebar-scroll">
            <div className="bots-md-section">
              <div className="bots-md-section-header tg">
                <i className="bi bi-telegram"></i>
                <span>Telegram</span>
                <span className="bots-md-section-count">{tgConfigured}/5</span>
              </div>
              {settings.telegram_bots.map((bot, index) => (
                <BotListRow
                  key={`telegram-${index}`}
                  bot={bot}
                  index={index + 1}
                  botType="telegram"
                  selected={selectedBot.botType === 'telegram' && selectedBot.index === index}
                  onSelect={() => { setSelectedBot({ botType: 'telegram', index }); setMobileView('detail'); }}
                />
              ))}
            </div>

            <div className="bots-md-section">
              <div className="bots-md-section-header max">
                <i className="bi bi-chat-dots"></i>
                <span>MAX</span>
                <span className="bots-md-section-count">{maxConfigured}/5</span>
              </div>
              {settings.max_bots.map((bot, index) => (
                <BotListRow
                  key={`max-${index}`}
                  bot={bot}
                  index={index + 1}
                  botType="max"
                  selected={selectedBot.botType === 'max' && selectedBot.index === index}
                  onSelect={() => { setSelectedBot({ botType: 'max', index }); setMobileView('detail'); }}
                />
              ))}
            </div>
          </div>

        </aside>

        {/* Detail: form for selected bot */}
        <main className="bots-md-detail">
          <BotDetailPane
            bot={selectedBot.botType === 'telegram'
              ? settings.telegram_bots[selectedBot.index]
              : settings.max_bots[selectedBot.index]}
            index={selectedBot.index + 1}
            botType={selectedBot.botType}
            groups={groups}
            showToken={showTokens[`${selectedBot.botType}-${selectedBot.index}`]}
            testNotice={testNotices[`${selectedBot.botType}-${selectedBot.index}`]}
            saving={saving}
            onToggleToken={() => toggleTokenVisibility(`${selectedBot.botType}-${selectedBot.index}`)}
            onUpdate={(field, value) => updateBotField(
              selectedBot.botType === 'telegram' ? 'telegram_bots' : 'max_bots',
              selectedBot.index, field, value
            )}
            onTest={() => handleTestBot(selectedBot.botType, selectedBot.index + 1, `${selectedBot.botType}-${selectedBot.index}`)}
            onSave={handleSave}
            onRefreshWebhook={(bid) => handleRefreshWebhook(bid, `${selectedBot.botType}-${selectedBot.index}`)}
            onBackToList={() => setMobileView('list')}
          />
        </main>
      </div>
    </div>
  );
};

interface BotListRowProps {
  bot: BotConfig;
  index: number;
  botType: 'telegram' | 'max';
  selected: boolean;
  onSelect: () => void;
}

const BotListRow: React.FC<BotListRowProps> = ({ bot, index, botType, selected, onSelect }) => {
  const isEmpty = bot.id == null && !bot.token && !bot.name;
  const resolvedName = bot.name || `${botType === 'telegram' ? 'TG' : 'MAX'} Bot ${index}`;
  const logoLetter = resolvedName.trim().charAt(0).toUpperCase() || (botType === 'telegram' ? 'T' : 'M');

  return (
    <div
      className={`bots-md-row ${selected ? 'is-selected' : ''} ${isEmpty ? 'is-empty' : ''} bot-${botType}`}
      onClick={onSelect}
    >
      <div className="bots-md-row-avatar">
        {!isEmpty && bot.logo_url ? (
          <img src={bot.logo_url} alt={resolvedName} onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />
        ) : (
          <span>{isEmpty ? '+' : logoLetter}</span>
        )}
      </div>
      <div className="bots-md-row-body">
        <div className="bots-md-row-name">
          #{index} {isEmpty ? <span className="bots-md-row-empty-label">Пусто</span> : resolvedName}
        </div>
        <div className="bots-md-row-sub">
          {isEmpty ? 'Слот свободен' : (
            <>
              <i className={`bi bi-circle-fill ${bot.enabled ? 'on' : 'off'}`}></i>
              {bot.enabled ? 'включён' : 'выключен'}
              {' · '}
              <span>{bot.total_messages ?? 0} сообщ.</span>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

interface BotDetailPaneProps {
  bot: BotConfig;
  index: number;
  botType: 'telegram' | 'max';
  groups: Group[];
  showToken: boolean;
  testNotice?: TestNotice;
  saving: boolean;
  onToggleToken: () => void;
  onUpdate: (field: string, value: any) => void;
  onTest: () => void;
  onSave: () => void;
  onRefreshWebhook: (botId: number | null | undefined) => void;
  onBackToList: () => void;
}

const BotDetailPane: React.FC<BotDetailPaneProps> = ({ bot, index, botType, groups, showToken, testNotice, saving, onToggleToken, onUpdate, onTest, onSave, onRefreshWebhook, onBackToList }) => {
  const logoInputRef = useRef<HTMLInputElement>(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoCacheBust, setLogoCacheBust] = useState(() => Date.now());

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !bot.id) return;
    setLogoUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const resp = await api.post(`/api/settings/bots/${bot.id}/logo`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onUpdate('logo_url', resp.data.logo_url);
      setLogoCacheBust(Date.now());
    } catch (err) {
      console.error('Logo upload failed:', err);
    } finally {
      setLogoUploading(false);
      if (logoInputRef.current) logoInputRef.current.value = '';
    }
  };

  const handleLogoDelete = async () => {
    if (!bot.id) return;
    setLogoUploading(true);
    try {
      await api.delete(`/api/settings/bots/${bot.id}/logo`);
      onUpdate('logo_url', null);
    } catch (err) {
      console.error('Logo delete failed:', err);
    } finally {
      setLogoUploading(false);
    }
  };

  const handleLogoFetch = async () => {
    if (!bot.id) return;
    setLogoUploading(true);
    try {
      const resp = await api.post(`/api/settings/bots/${bot.id}/fetch-logo`);
      onUpdate('logo_url', resp.data.logo_url);
      setLogoCacheBust(Date.now());
    } catch (err: any) {
      const msg = err?.response?.data?.detail || 'Ошибка получения аватара бота';
      console.error('Logo fetch failed:', msg);
      alert(msg);
    } finally {
      setLogoUploading(false);
    }
  };

  const runtimeDomain = (window.location?.hostname || 'example.com').trim();
  const webhookBasePlaceholder = `https://${runtimeDomain}/webhook/max`;
  const resolvedBotName = bot.name || `${botType === 'telegram' ? 'Telegram' : 'MAX'} Bot ${index}`;
  const logoLetter = resolvedBotName.trim().charAt(0).toUpperCase() || (botType === 'telegram' ? 'T' : 'M');

  return (
    <div className={`bot-pane bot-pane-${botType}`}>
      <div className="bot-pane-header">
        <button className="bot-pane-back-btn" onClick={onBackToList} title="К списку">
          <i className="bi bi-arrow-left"></i>
        </button>
        <div className="bot-pane-avatar-wrap">
          <div className="bot-pane-avatar">
            {bot.logo_url ? (
              <img src={`${bot.logo_url}?t=${logoCacheBust}`} alt={resolvedBotName} onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />
            ) : (
              <span>{logoLetter}</span>
            )}
          </div>
          {bot.id && (
            <>
              <button
                type="button"
                className={`bot-avatar-mini-btn upload${logoUploading ? ' uploading' : ''}`}
                onClick={() => logoInputRef.current?.click()}
                disabled={logoUploading}
                title={bot.logo_url ? 'Сменить логотип' : 'Загрузить логотип с компьютера'}
              >
                <i className={`bi ${logoUploading ? 'bi-hourglass-split' : 'bi-camera-fill'}`}></i>
              </button>
              {bot.token && !logoUploading && (
                <button
                  type="button"
                  className="bot-avatar-mini-btn fetch"
                  onClick={handleLogoFetch}
                  title={`Загрузить аватар из ${botType === 'telegram' ? 'Telegram' : 'MAX'}`}
                >
                  <i className="bi bi-arrow-down-circle-fill"></i>
                </button>
              )}
              {bot.logo_url && !logoUploading && (
                <button
                  type="button"
                  className="bot-avatar-mini-btn delete"
                  onClick={handleLogoDelete}
                  title="Удалить логотип"
                >
                  <i className="bi bi-x-lg"></i>
                </button>
              )}
              <input ref={logoInputRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp" style={{ display: 'none' }} onChange={handleLogoUpload} />
            </>
          )}
        </div>
        <div className="bot-pane-title">
          <h3>{botType === 'telegram' ? 'Telegram' : 'MAX'} бот #{index}</h3>
          <small>{resolvedBotName}</small>
        </div>
        <div className="bot-pane-header-actions">
          <button className="btn btn-secondary" onClick={() => onRefreshWebhook(bot.id)} disabled={!bot.id || !bot.webhook_url} title="Перерегистрировать webhook на платформе">
            <i className="bi bi-arrow-repeat"></i>
            Webhook
          </button>
          <button className="btn btn-secondary" onClick={onTest} disabled={!bot.enabled || !bot.token} title="Проверить соединение">
            <i className="bi bi-lightning"></i>
            Тест
          </button>
          <button className="btn btn-primary" onClick={onSave} disabled={saving} title="Сохранить все настройки">
            {saving ? (
              <><i className="bi bi-hourglass-split"></i> Сохранение...</>
            ) : (
              <><i className="bi bi-check-lg"></i> Сохранить</>
            )}
          </button>
        </div>
      </div>

      {testNotice && (
        <div className={`bot-pane-notice ${testNotice.type}`}>
          <i className={`bi bi-${testNotice.type === 'success' ? 'check-circle-fill' : 'exclamation-triangle-fill'}`}></i>
          {testNotice.text}
        </div>
      )}

      <div className="bot-pane-body">
          <div className="form-group">
            <label>Включен</label>
            <label className="toggle-switch">
              <input type="checkbox" checked={bot.enabled} onChange={(e) => onUpdate('enabled', e.target.checked)} />
              <span className="toggle-slider"></span>
            </label>
          </div>

          <div className="form-group">
            <label>Название</label>
            <input type="text" className="form-control" placeholder={`${botType === 'telegram' ? 'Telegram' : 'MAX'} Bot ${index}`} value={bot.name} onChange={(e) => onUpdate('name', e.target.value)} />
          </div>

          <div className="form-group">
            <label>Ссылка</label>
            <input type="text" className="form-control" placeholder={botType === 'telegram' ? 'https://t.me/...' : 'https://max.ru/id...'} value={bot.link} onChange={(e) => onUpdate('link', e.target.value)} />
          </div>

          {botType === 'max' && (
            <>
              <div className="form-group">
                <label>MAX Bot ID</label>
                <input type="text" className="form-control" placeholder="id1234567890_bot" value={bot.bot_id || ''} onChange={(e) => onUpdate('bot_id', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Username / Alias</label>
                <input type="text" className="form-control" placeholder="@max_bot_username" value={bot.username || ''} onChange={(e) => onUpdate('username', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Webhook URL</label>
                <input type="text" className="form-control" placeholder={webhookBasePlaceholder} value={bot.webhook_url || ''} onChange={(e) => onUpdate('webhook_url', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Webhook Token <small>(optional)</small></label>
                <input type="text" className="form-control" placeholder="будет сгенерирован автоматически" value={bot.webhook_token || ''} onChange={(e) => onUpdate('webhook_token', e.target.value)} />
              </div>
            </>
          )}

          <div className="form-group">
            <label>Описание</label>
            <textarea className="form-control" rows={2} placeholder="Описание бота" value={bot.description} onChange={(e) => onUpdate('description', e.target.value)} />
          </div>

          <div className="form-group">
            <label>Bot Token</label>
            <div className="input-group">
              <input type={showToken ? "text" : "password"} className="form-control" placeholder="Введите токен" value={bot.token} onChange={(e) => onUpdate('token', e.target.value)} />
              <button className="btn btn-outline-secondary" onClick={onToggleToken} type="button" disabled={!bot.token}>
                <i className={`bi bi-eye${showToken ? '-slash' : ''}`}></i>
              </button>
            </div>
          </div>

          <div className="bot-pane-meta">
            <span>ID: <code>{bot.id ?? '—'}</code></span>
            <span>Сообщений: <code>{bot.total_messages ?? 0}</code></span>
            <span>Пользователей: <code>{bot.total_users ?? 0}</code></span>
            <span>Платформа: <code>{bot.platform || botType}</code></span>
          </div>

          <div className="bot-pane-hint">
            <i className="bi bi-info-circle"></i>
            Изменения применяются после нажатия «Сохранить» в левой панели
          </div>
      </div>
    </div>
  );
};

export default SettingsMultiple;
